import { useState, memo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Country code to display name
const countryNames = {
  USA: 'United States', CAN: 'Canada', MEX: 'Mexico', BRA: 'Brazil', ARG: 'Argentina',
  GBR: 'United Kingdom', FRA: 'France', DEU: 'Germany', ESP: 'Spain', ITA: 'Italy',
  SWE: 'Sweden', NOR: 'Norway', FIN: 'Finland', RUS: 'Russia', CHN: 'China',
  JPN: 'Japan', KOR: 'South Korea', IND: 'India', AUS: 'Australia', ZAF: 'South Africa',
  EGY: 'Egypt', NGA: 'Nigeria', SAU: 'Saudi Arabia', ARE: 'UAE', ISR: 'Israel',
  TUR: 'Turkey', POL: 'Poland', NLD: 'Netherlands', BEL: 'Belgium', CHE: 'Switzerland',
  AUT: 'Austria', CZE: 'Czechia', GRC: 'Greece', PRT: 'Portugal', IRL: 'Ireland',
  DNK: 'Denmark', NZL: 'New Zealand', SGP: 'Singapore', THA: 'Thailand', VNM: 'Vietnam',
  IDN: 'Indonesia', PHL: 'Philippines', MYS: 'Malaysia', PAK: 'Pakistan', BGD: 'Bangladesh',
  CHL: 'Chile', COL: 'Colombia', PER: 'Peru', VEN: 'Venezuela', UKR: 'Ukraine',
  ROU: 'Romania', HUN: 'Hungary', UK: 'United Kingdom',
  US: 'United States', CA: 'Canada', MX: 'Mexico', BR: 'Brazil', AR: 'Argentina',
  GB: 'United Kingdom', FR: 'France', DE: 'Germany', ES: 'Spain', IT: 'Italy',
  SE: 'Sweden', NO: 'Norway', FI: 'Finland', RU: 'Russia', CN: 'China',
  JP: 'Japan', KR: 'South Korea', IN: 'India', AU: 'Australia', ZA: 'South Africa',
  EG: 'Egypt', NG: 'Nigeria', SA: 'Saudi Arabia', AE: 'UAE', IL: 'Israel',
  TR: 'Turkey', PL: 'Poland', NL: 'Netherlands', BE: 'Belgium', CH: 'Switzerland',
  AT: 'Austria', CZ: 'Czechia', GR: 'Greece', PT: 'Portugal', IE: 'Ireland',
  DK: 'Denmark', NZ: 'New Zealand', SG: 'Singapore', TH: 'Thailand', VN: 'Vietnam',
  ID: 'Indonesia', PH: 'Philippines', MY: 'Malaysia', PK: 'Pakistan', BD: 'Bangladesh',
  CL: 'Chile', CO: 'Colombia', PE: 'Peru', VE: 'Venezuela', UA: 'Ukraine',
  RO: 'Romania', HU: 'Hungary'
};

// Map from country name (as appears in TopoJSON) to our country codes
const nameToCode = {
  'United States of America': 'US', 'United States': 'US', 'USA': 'US',
  'United Kingdom': 'GB', 'UK': 'GB', 'Great Britain': 'GB', 'England': 'GB',
  'Canada': 'CA', 'Mexico': 'MX', 'Brazil': 'BR', 'Argentina': 'AR',
  'France': 'FR', 'Germany': 'DE', 'Spain': 'ES', 'Italy': 'IT',
  'Sweden': 'SE', 'Norway': 'NO', 'Finland': 'FI', 'Russia': 'RU',
  'China': 'CN', 'Japan': 'JP', 'South Korea': 'KR', 'Korea': 'KR',
  'India': 'IN', 'Australia': 'AU', 'South Africa': 'ZA',
  'Egypt': 'EG', 'Nigeria': 'NG', 'Saudi Arabia': 'SA',
  'United Arab Emirates': 'AE', 'UAE': 'AE', 'Israel': 'IL',
  'Turkey': 'TR', 'Türkiye': 'TR', 'Poland': 'PL', 'Netherlands': 'NL',
  'Belgium': 'BE', 'Switzerland': 'CH', 'Austria': 'AT',
  'Czech Republic': 'CZ', 'Czechia': 'CZ', 'Greece': 'GR',
  'Portugal': 'PT', 'Ireland': 'IE', 'Denmark': 'DK',
  'New Zealand': 'NZ', 'Singapore': 'SG', 'Thailand': 'TH',
  'Vietnam': 'VN', 'Viet Nam': 'VN', 'Indonesia': 'ID',
  'Philippines': 'PH', 'Malaysia': 'MY', 'Pakistan': 'PK',
  'Bangladesh': 'BD', 'Chile': 'CL', 'Colombia': 'CO',
  'Peru': 'PE', 'Venezuela': 'VE', 'Ukraine': 'UA',
  'Romania': 'RO', 'Hungary': 'HU'
};

// Convert 2-letter to 3-letter ISO code
const toISO3 = (code) => {
  if (code?.length === 2) {
    return countryNames[code] || code;
  }
  return code;
};

export const getCountryName = (code) => {
  const iso3 = toISO3(code);
  return countryNames[iso3] || code;
};

const WorldMap = memo(({ data = [], onCountryClick }) => {
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Create lookup by country code (normalized to uppercase)
  const countryData = {};
  let maxCount = 1;
  data.forEach(item => {
    const code = item.country?.toUpperCase();
    if (code) {
      countryData[code] = item.count;
      // Also store the display name version
      const displayName = countryNames[code];
      if (displayName) countryData[displayName] = item.count;
    }
    if (item.count > maxCount) maxCount = item.count;
  });

  // Get count for a geography by trying name-based lookup
  const getCountForGeo = (geo) => {
    const name = geo.properties.name;
    // Try direct name match
    if (countryData[name]) return countryData[name];
    // Try converting name to code
    const code = nameToCode[name];
    if (code && countryData[code]) return countryData[code];
    return 0;
  };

  const getColor = (geo) => {
    const count = getCountForGeo(geo);
    if (count === 0) return '#1a1a1a';

    // Log scale for better visibility
    const logMax = Math.log(maxCount + 1);
    const logCount = Math.log(count + 1);
    const intensity = logCount / logMax;

    // Range from dark gray to bright white
    const val = Math.round(60 + intensity * 195);
    return `rgb(${val}, ${val}, ${val})`;
  };

  const handleMouseEnter = (geo, evt) => {
    const name = geo.properties.name;
    const count = getCountForGeo(geo);

    if (count > 0) {
      setTooltipContent(`${name}: ${count} active`);
      setTooltipPos({ x: evt.clientX, y: evt.clientY });
    }
  };

  const handleMouseLeave = () => {
    setTooltipContent('');
  };

  const handleClick = (geo) => {
    const count = getCountForGeo(geo);
    if (count > 0 && onCountryClick) {
      const name = geo.properties.name;
      const code = nameToCode[name];
      // Find original country code from data
      const original = data.find(d =>
        d.country?.toUpperCase() === code ||
        countryNames[d.country?.toUpperCase()] === name
      );
      if (original) {
        onCountryClick(original.country);
      }
    }
  };

  return (
    <div className="AP-world-map-container">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 130,
          center: [0, 30]
        }}
        width={900}
        height={450}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomableGroup>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const hasData = getCountForGeo(geo) > 0;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getColor(geo)}
                    stroke="#2a2a2a"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: {
                        fill: hasData ? '#fff' : '#252525',
                        outline: 'none',
                        cursor: hasData ? 'pointer' : 'default'
                      },
                      pressed: { outline: 'none' }
                    }}
                    onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleClick(geo)}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {tooltipContent && (
        <div
          className="AP-map-tooltip"
          style={{
            left: tooltipPos.x + 10,
            top: tooltipPos.y - 30,
            position: 'fixed'
          }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
});

export default WorldMap;
